<template>
    <b-card class="p-2 w-100 bg-white" body-class="d-flex flex-column border rounded p-0 my-0 shadow-sm">
        <b-card-header header-tag="header" class="text-center font-weight-bold py-1 mx-0 w-100 bg-white">
            <b-row align-v="center">
                <b-col cols="auto">
                    <small class="pl-0 pr-3 font-weight-bold">{{$t('Period')}}</small>
                </b-col>
                <b-col class="px-2">
                    <b-button-group class="d-flex btn-gray-light rounded-pill border border-gray">
                        <!-- 'Simple' -->
                        <b-button v-if="hasSimpleMode" @click="setPeriodMode(modeEnum.Simple)" :pressed="period.SelectedMode === modeEnum.Simple" pill variant="outline-white" 
                        size="sm" :class="[ 'periodMode border-0', {'text-black' : period.SelectedMode != modeEnum.Simple}]">{{$t('Simple')}}</b-button> 
                        <!-- 'Avancée' -->
                        <b-button v-if="hasAdvancedMode" @click="setPeriodMode(modeEnum.Advanced)" :pressed="period.SelectedMode === modeEnum.Advanced" pill variant="outline-white" 
                        size="sm" :class="[ 'periodMode border-0', {'text-black' : period.SelectedMode != modeEnum.Advanced}]" >{{$t('Advanced')}}</b-button> 
                    </b-button-group>
                </b-col>
            </b-row>
        </b-card-header>
        <b-card-body class="py-0 bg-white">
            <b-row class="py-0">
                <!-- Mode Simple (glissant) -->
                <b-card v-if="period.SelectedMode === modeEnum.Simple" class="py-0 w-100 bg-white" body-class="d-flex flex-column no-border px-0 py-1 my-0">
                    <b-card-body class="py-0">
                        <!-- Sélection du type de période -->
                        <b-row class="px-2">
                            <b-button v-for="periodType in filteredPeriods" :key="periodType.Id" @click="setPeriodType(periodType.Id)" :pressed="period.SelectedPeriod === periodType.Id" size="sm" class="mr-1 mb-1 flex-grow-1" variant="outline-gray-dark text-black">
                                {{ periodType.Label }}
                            </b-button>
                        </b-row>

                        <!-- saisie de la période glissante -->
                        <b-row v-if="hasManualEntryDuration" class="px-2 pt-2" align-v="center">
                            <b-col cols="2">
                                <label :for="$id('slidingDuration')" class="m-0">{{$t('Duration')}}</label>
                            </b-col>
                            <b-col cols="10">
                                <b-input-group >
                                    <b-form-input :id="$id('slidingDuration')" number min=1 max=100 size="sm" no-wheel v-model="period.Duration" :formatter="onlyNumber" />
                                    <template #append>
                                        <b-dropdown :text="period.TimeUnit.Label" variant="gray" size="sm">
                                            <b-dropdown-item-button @click="period.TimeUnit = u;" v-for="u in slidingUnits" :key="u.Id" >
                                                {{ u.Label }}
                                            </b-dropdown-item-button>
                                        </b-dropdown>
                                    </template>
                                </b-input-group>
                            </b-col>
                        </b-row>
                    </b-card-body>
                </b-card>
                <!-- Mode avancé / fixe -->
                <b-card v-else class="py-0 w-100" body-class="d-flex bg-white flex-column no-border px-0 py-1 my-0">
                    <b-card-body class="py-0">
                        <!-- Selection du type de période -->
                        <b-row class="px-2">
                            <!-- 'Dates' -->
                            <b-button v-for="periodType in filteredPeriods" :key="periodType.Id" @click="period.SelectedPeriod = periodType.Id; " :pressed="period.SelectedPeriod === periodType.Id" size="sm" class="mr-1 mb-1 flex-grow-1" variant="outline-gray-dark text-black">
                                {{ periodType.Id != 'Day' ? periodType.Label : $t('Dates') }}
                            </b-button>
                        </b-row>

                        <hr class="pt-1 mb-0" />

                        <b-row class="py-2 ">
                            <!-- 'Nouvelle Période' -->
                            <b-button @click="addAnotherPeriod"  size="sm" class="mx-2 font-weight-semibold" :variant="'outline-' + Variant" pill><b-icon icon="plus-lg" scale="0.8" /> {{$t('NewPeriod')}}</b-button>
                        </b-row>

                        <!-- Gestion de périodes -->
                        <b-row class="py-0">
                            <!-- Formulaire année -->
                            <b-col v-if="period.SelectedPeriod == 'Year'">
                                <template v-for="(input, index) in selectedYears">
                                    <b-row cols="2" class="pb-2" align-v="center">
                                        <b-col cols="2" class="px-0 text-center">
                                            <b-icon role="button" class="text-danger" icon="x" scale="2" v-if="selectedYears.length > 1" @click="deletePeriodInput(index)" />
                                        </b-col>
                                        <b-col cols="10" class="pl-0 pr-3">
                                            <b-row>
                                                <b-col class="pl-1">
                                                    <b-form-select id="start"
                                                        :options="availableYears"
                                                        value-field="Id"
                                                        text-field="Label"
                                                        size="sm"
                                                        plain
                                                        v-model="input.value"
                                                        :state="periodValid"
                                                        class="w-100"/>
                                                </b-col>
                                            </b-row>
                                        </b-col>
                                    </b-row>
                                </template>
                            </b-col>
                            <!-- Formulaire mois -->
                            <b-col v-else-if="period.SelectedPeriod == 'Month'">
                                <template v-for="(input, index) in selectedMonths">
                                    <b-row cols="2" class="pb-2" align-v="center">
                                        <b-col cols="2" class="px-0 text-center">
                                            <b-icon role="button" class="text-danger" icon="x" scale="2" v-if="selectedMonths.length > 1" @click="deletePeriodInput(index)" />
                                        </b-col>
                                        <b-col cols="10" class="pl-0 pr-3">
                                            <b-row>
                                                <b-col class="px-1">
                                                    <b-form-select :options="availableMonths"
                                                        value-field="Id"
                                                        text-field="Label"
                                                        size="sm"
                                                        plain
                                                        v-model="input.month"
                                                        :state="periodValid"
                                                        class="w-100"/>
                                                </b-col>
                                                <b-col class="pl-1">
                                                    <b-form-select :options="availableYears"
                                                        value-field="Id"
                                                        text-field="Label"
                                                        size="sm"
                                                        plain
                                                        v-model="input.year"
                                                        :state="periodValid"
                                                        class="w-100"/>
                                                </b-col>
                                            </b-row>
                                        </b-col>
                                    </b-row>
                                </template>
                            </b-col>
                            <!-- Formulaire semaine -->
                            <b-col v-else-if="period.SelectedPeriod == 'Week'">
                                <template v-for="(input,index) in selectedWeeks">
                                    <b-row cols="2" class="pb-2" align-v="center">
                                        <b-col cols="2" class="px-0 text-center">
                                            <b-icon role="button" class="text-danger" icon="x" scale="2" v-if="selectedWeeks.length > 1" @click="deletePeriodInput(index)" />
                                        </b-col>
                                        <b-col cols="10" class="pl-0 pr-3">
                                            <b-row>
                                                <b-col class="px-1">
                                                    <b-form-select size="sm" plain v-model="input.week">
                                                        <b-form-select-option v-for="n in getWeekNumber(input.year)" :key="n" :value="n" >{{$t('WeekLabel', { number: n })}}</b-form-select-option>
                                                    </b-form-select>
                                                </b-col>
                                                <b-col class="pl-1">
                                                    <b-form-select :options="availableYears"
                                                        value-field="Id"
                                                        text-field="Label"
                                                        size="sm"
                                                        plain
                                                        v-model="input.year"
                                                        :state="periodValid"
                                                        class="w-100" />
                                                </b-col>
                                            </b-row>
                                        </b-col>
                                    </b-row>
                                </template>
                            </b-col>

                            <!-- Formulaire dates -->
                            <b-col v-else-if="period.SelectedPeriod == 'Day'">
                                <template v-for="(input, index) in selectedDays">
                                    <b-row cols="2" class="pb-2" align-v="center">
                                        <b-col cols="2" class="px-0 text-center">
                                            <b-icon role="button" class="text-danger" icon="x" scale="2" v-if="selectedDays.length > 1" @click="deletePeriodInput(index)" />
                                        </b-col>
                                        <b-col cols="10" class="pl-0 pr-3">
                                            <b-row>
                                                <b-col cols="6" class="px-1">
                                                    <b-input-group>
                                                        <b-form-input
                                                            v-model="input.start"
                                                            button-only
                                                            right
                                                            locale="fr"
                                                            size="sm"
                                                            type="date"
                                                            :state="periodValid"
                                                        ></b-form-input>
                                                    </b-input-group>
                                                </b-col>
                                                <b-col cols="6" class="pl-1">
                                                    <b-input-group>
                                                        <b-form-input
                                                            v-model="input.end"
                                                            button-only
                                                            right
                                                            locale="fr"
                                                            size="sm"
                                                            type="date"
                                                            :state="periodValid"
                                                        ></b-form-input>
                                                    </b-input-group>
                                                </b-col>
                                            </b-row>
                                        </b-col>
                                    </b-row>
                                </template>
                            </b-col>
                            <!-- Formulaire Horaire -->
                            <b-col v-else-if="period.SelectedPeriod == 'Hour' || period.SelectedPeriod == 'Minutes30'
                                                    || period.SelectedPeriod == 'Minutes10'">
                                <template v-for="(input, index) in selectedHours">
                                    <b-row cols="2" class="pb-2" align-v="center">
                                        <b-col cols="2" class="px-0 text-center">
                                            <b-icon role="button" class="text-danger" icon="x" scale="2"
                                                    v-if="selectedHours.length > 1"
                                                    @click="deletePeriodInput(index)"/>
                                        </b-col>
                                        <b-col cols="10" class="pl-0">
                                            <b-row>
                                                <b-col class="px-1 pr-3">
                                                    <b-input-group>
                                                        <b-form-input
                                                            v-model="input.start"
                                                            button-only
                                                            right
                                                            locale="fr"
                                                            size="sm"
                                                            type="date"
                                                            :state="periodValid"
                                                        ></b-form-input>
                                                        <b-form-input
                                                            v-model="input.startHour"
                                                            button-only
                                                            right
                                                            locale="fr"
                                                            size="sm"
                                                            type="time"
                                                            :state="periodValid"
                                                        ></b-form-input>
                                                    </b-input-group>
                                                </b-col>
                                            </b-row>
                                            <b-row>
                                                <b-col class="pl-1 pr-3">
                                                    <b-input-group>
                                                        <b-form-input
                                                            v-model="input.end"
                                                            button-only
                                                            right
                                                            locale="fr"
                                                            size="sm"
                                                            type="date"
                                                            :state="periodValid"
                                                        ></b-form-input>
                                                        <b-form-input
                                                            v-model="input.endHour"
                                                            button-only
                                                            right
                                                            locale="fr"
                                                            size="sm"
                                                            type="time"
                                                            :state="periodValid"
                                                        ></b-form-input>
                                                    </b-input-group>
                                                </b-col>
                                            </b-row>
                                        </b-col>
                                    </b-row>
                                </template>
                            </b-col>
                        </b-row>
                    </b-card-body>
                </b-card>
                <!-- Tendance -->
                <TrendPeriod 
                    v-if="HasTrend || (HasTrendOnSimpleMode && period.SelectedMode === modeEnum.Simple) || (HasTrendOnAdvancedMode && period.SelectedMode === modeEnum.Advanced)"
                    :Configs.sync="configs" 
                    :UseTrend.sync="useTrend" 
                    :Variant="Variant" 
                    :HasPreviousTrend="hasPreviousTrend"
                    :HasLastYearTrend="hasLastYearTrend"
                    :HasRefYearTrend="hasRefYearTrend"
                    :HasLastYear="hasLastYear"
                    :HasRefPeriod="hasRefPeriod"
                    :HasReferencePeriod="hasReferencePeriod"
                    :PeriodTypes="periodTypes"
                    :Period.sync="period"
                    :HasTrendType="hasTrendType"
                    :AvailableYears.sync="availableYears"
                    :AvailableMonths.sync="availableMonths"
                    :RefYear="refYear"
                    :RefMonth="refMonth"
                    :RefWeek="refWeek"
                    :RefDay="refDay"
                    :RefValid="refValid"/>
                <!-- Durée de la période vide -->
                <b-form-invalid-feedback v-if="period.SelectedMode === modeEnum.Simple" :state="durationValid" class="text-center">
                     <!-- 'Saisie de la durée de la période glissante est plus grande que l'ensemble des données disponibles.' / 'La durée de la période ne peut pas être vide ou égale à 0.' -->
                    {{ isDurationGreater ? $t('Errors.DurationTooLarge') : $t('Errors.EmptyDuration') }} 
                </b-form-invalid-feedback>
                <!-- Période présente plusieurs fois en mode avancée -->
                <b-form-invalid-feedback v-if="period.SelectedMode === modeEnum.Advanced" :state="periodValid" class="text-center">
                    <!-- 'Une même période ne peut pas être renseignée plusieurs fois.' -->
                    {{$t('Errors.DuplicatePeriods')}}
                </b-form-invalid-feedback>
            </b-row>
        </b-card-body>
    </b-card>
</template>

<script lang="ts">
    import { Component, Watch, Vue } from 'vue-property-decorator';
    import PeriodConfig from './PeriodConfig';
    import TrendPeriod from './TrendPeriod.vue'
    import ModeEnum from '@/enums/ModeEnum';
    import BasePeriod from '@/types/Config/BasePeriod';
    import Sliding from '@/types/Config/Sliding';
    import SlidingTrend from '@/types/Config/SlidingTrend';
    import Range from '@/types/Config/Range';
    import DateToDate from '@/types/Config/DateToDate';
    import moment from 'moment';
    /** Traductions */
    import locales from './locales/MultiPeriod.json';
    import localesPeriodConfig from "./locales/PeriodConfig.json";

    /** Paramétrage du composant MultiPeriod */
    @Component({
		components: {
			PeriodConfig,
            TrendPeriod
		},
        i18n: { messages: locales, sharedMessages : localesPeriodConfig }
	})
	export default class MultiPeriod extends PeriodConfig {
        /** Utilisation d'une tendance */
        private useTrend: boolean = false;

        /** Permet l'utilisation de l'enum dans template
         ** Enumeration pour le mode de période (Simple ou Avancé)
         **/
        private modeEnum = ModeEnum;

        /**
         * Met à jour le mode de période
         * @param modeSelected Valeur de l'Enum de ModeEnum
         */
         private setPeriodMode(modeSelected: ModeEnum) {
            if(modeSelected === ModeEnum.Advanced){
                this.isDurationGreater = false;
                this.initdefaultAdvancedPeriods();
            }

            this.period.SelectedMode = modeSelected;
            this.period.SelectedPeriod = "Year";
        }

        /**
         * Met à jour le type de période et certaines variables
         * @param periodType type de période 
         */
        private setPeriodType(periodType){
            // Mise à jour type de période
            this.period.SelectedPeriod = periodType;
        }

        /**
         * Validité de la période de référence
         * @description (est à null par défaut pour pas afficher l'invalidité)
         */
         private refValid: boolean = null;

        /**
        * Validité de la durée pour le mode simple
        * @description (est à null par défaut pour pas afficher l'invalidité)
        */
        private durationValid : boolean = null;

        /**
        * Validité des périodes du mode avancée
        * @description (est à null par défaut pour pas afficher l'invalidité)
        */
        private periodValid: boolean = null;

        // Indication si la durée renseignée est plus grande que l'ensemble des données disponibles
        private isDurationGreater : boolean = false;

        /** Liste des périodes  */
        private periodTypes: { Id: string, Label: string }[] = [
            {Id: 'Year', Label: this.$t('PeriodTypes.Year').toString()},
            {Id: 'Month', Label: this.$t('PeriodTypes.Month').toString()},
            {Id: 'Week', Label: this.$t('PeriodTypes.Week').toString()},
            {Id: 'Day', Label: this.$t('PeriodTypes.Day').toString()},
            {Id: 'Hour', Label: this.$t('PeriodTypes.Hour').toString()},
            {Id: 'Minutes30', Label: this.$t('PeriodTypes.Minutes30').toString()},
            {Id: 'Minutes10', Label: this.$t('PeriodTypes.Minutes10').toString()}
        ]

        /** Liste des périodes simples filtrées */
        private filteredPeriods: { Id: string, Label: string }[] = [];

        /** Liste des unites glissantes */
        private slidingUnits: { Id: string, Label: string }[] = [
            {Id: 'Year', Label: this.$t('TimeUnits.LastYears').toString()},
            {Id: 'Month', Label: this.$t('TimeUnits.LastMonths').toString()},
            {Id: 'Week', Label: this.$t('TimeUnits.LastWeeks').toString()},
            {Id: 'Day', Label: this.$t('TimeUnits.LastDays').toString()}
        ]

        /** Date du jour */
        private today: Date = new Date();

        /** Date de la veille */
        public yesterday: Date = new Date(Date.now() - 86400000); // that is: 24 * 60 * 60 * 1000

        /** Valeur de l'année de référence (utilisé aussi pour le mois et la semaine) */
        private refYear: string = this.today.getFullYear().toString();

        /** Valeur du mois de référence */
        private refMonth: string = this.getMonths()[0].Id;

        /** Valeur de la semaine de référence */
        private refWeek: string = "1";

        /** Valeur du jour de référence */
        private refDay: Date = new Date(this.today.getFullYear(), 0, 1);

        /** Année de départ pour la fonction getYears() de PeriodConfig */
        private startYear : number = 0;

        /** Années disponibles */
        private availableYears : {Id: number, Label: string}[] = []; //this.getYears(this.nbYears);

        /** Mois disponibles */
        private availableMonths = this.getMonths();

        /** Liste de périodes par défaut sélectionnées niveau annuel */
        private selectedYears: { value: number}[] = [];

        /** liste des périodes par défaut sélectionnées niveau mensuel */
        private selectedMonths: { month: string, year:number}[] = []

        /** liste des périodes par défaut sélectionnées niveau hebdomadaire */
        private selectedWeeks: { week: number, year: number }[] = []

        /** liste des périodes par défaut sélectionnées niveau journalier */
        private selectedDays: { start: string, end: string }[] = [
            { start: this.yesterday.toISOString().split('T')[0] , end: this.today.toISOString().split('T')[0] },
            { start: new Date(Date.UTC(this.yesterday.getFullYear() - 1 , this.yesterday.getMonth(), this.yesterday.getDate())).toISOString().split('T')[0] , end: new Date(Date.UTC(this.today.getFullYear() - 1 , this.today.getMonth(), this.today.getDate())).toISOString().split('T')[0] },
            { start: new Date(Date.UTC(this.yesterday.getFullYear() - 2 , this.yesterday.getMonth(), this.yesterday.getDate())).toISOString().split('T')[0] , end: new Date(Date.UTC(this.today.getFullYear() - 2 , this.today.getMonth(), this.today.getDate())).toISOString().split('T')[0] },
        ]

        /** liste des périodes par défaut sélectionnées niveau horaire (horaire, 30 minutes et 10 minutes)*/
        private selectedHours: { start: string, startHour: string, end: string, endHour: string }[] = [{
            start: this.yesterday.toISOString().split('T')[0],
            startHour: this.yesterday.toLocaleTimeString('fr-FR').substr(0, 5),
            end: this.today.toISOString().split('T')[0],
            endHour: this.today.toLocaleTimeString('fr-FR').substr(0, 5)
        }];

        /** Objet représentant les périodes sélectionnées pour les multi-périodes */
        private period : Period = {
            SelectedMode : undefined,
            SelectedPeriod : undefined,
            LastYearPeriod : undefined,
            RefPeriod: undefined,
            Duration : undefined,
            SelectedYears : this.selectedYears,
            SelectedMonths : this.selectedMonths,
            SelectedWeeks : this.selectedWeeks,
            SelectedDays : this.selectedDays,
            SelectedHours : this.selectedHours,
            TimeUnit: this.slidingUnits[0],
        };

        /** Liste des périodes sélectonnées */
        private Periods: BasePeriod[] = [];

        /** Ajout d'une nouvelle période en mode avancé selon le type sélectionné */
        private addAnotherPeriod() {

            let lastEl: any;

            switch (this.period.SelectedPeriod) {
                case 'Year':
                    lastEl = this.selectedYears.slice(-1);

                    if(this.availableYears.findIndex(x => x.Id == lastEl[0].value - 1) != -1)
                        this.selectedYears.push({ value: lastEl[0].value - 1 });

                    break;
                case 'Month':
                    lastEl = this.selectedMonths.slice(-1);

                    this.selectedMonths.push({ month: this.availableMonths[0].Id, year: lastEl[0].year - 1  });

                    break;
                case 'Week':
                    lastEl = this.selectedWeeks.slice(-1);

                    this.selectedWeeks.push({ week: 1, year: lastEl[0].year - 1 });
                    break;
                case 'Day':
                    lastEl = this.selectedDays.slice(-1);

                    let start: number[] = lastEl[0].start.split('-');
                    let end: number[] = lastEl[0].end.split('-');

                    this.selectedDays.push({ start: new Date(Date.UTC(start[0] - 1, start[1] - 1, start[2])).toISOString().split('T')[0], end: new Date(Date.UTC(end[0] - 1, end[1] - 1, end[2])).toISOString().split('T')[0] });
                    break;
                case 'Hour':
                case "Minutes30":
                case "Minutes10":
                        lastEl = this.selectedHours.slice(-1);

                        let startHour: number[] = lastEl[0].start.split('-');
                        let endHour: number[] = lastEl[0].end.split('-');

                        this.selectedHours.push({
                            start: new Date(Date.UTC(startHour[0] - 1, startHour[1] - 1, startHour[2])).toISOString().split('T')[0],
                            startHour: lastEl[0].startHour,
                            end: new Date(Date.UTC(endHour[0] - 1, endHour[1] - 1, endHour[2])).toISOString().split('T')[0],
                            endHour: lastEl[0].endHour
                        });
                    break;
                }
        }

        /**
        * Suppresion d'une periode selon son index et le type sélectionné
        * @param index index de la liste
        */
        private deletePeriodInput(index:number) {

            switch (this.period.SelectedPeriod) {
                case 'Year':
                    this.selectedYears.splice(index, 1);
                    break;
                case 'Month':
                    this.selectedMonths.splice(index, 1);
                    break;
                case 'Week':
                    this.selectedWeeks.splice(index, 1);
                    break;
                case 'Day':
                    this.selectedDays.splice(index, 1);
                    break;
                case 'Hour':
                case "Minutes30":
                case "Minutes10":
                    this.selectedHours.splice(index, 1);
                break;
            }
        }

        /**
         * Permet de charger les périodes selon les parametres renseignés
         * @param parameters Objet des paramètres des périodes
         */
        private loadPeriods(parameters) {
            console.debug("MultiPeriod - loadPeriods() - parameters : %O", parameters);
            /** Initialisation des périodes*/
            // verifie si parameters['Periods'] existe
            // selection periode année + affichage liste deroulante correspondante
            // range => start ou end car identique
            if (parameters && "Periods" in parameters && parameters['Periods']) {

                // Sélection mode Simple/Avancé
                this.period.SelectedMode = (ModeEnum[parameters["Periods"][0].Mode] as unknown as ModeEnum);

                console.debug("MultiPeriod - loadPeriods() - parameters[Periods] - ", parameters["Periods"]);
                let value: number;

                //mode simple
                if(this.period.SelectedMode === ModeEnum.Simple)
                {
                    // Récupération différentes périodes (glissante, année précédente, ref)
                    let slidingPeriod : Sliding = parameters['Periods'].filter(x => x.Type.match(/Sliding$/))[0];
                    let lastYearPeriod : SlidingTrend = parameters['Periods'].filter(x => x.Type.match(/SlidingTrend$/))[0];
                    let refPeriod : BasePeriod = parameters['Periods'].filter(x => !x.Type.includes('Sliding'))[0];

                    console.debug("MultiPeriod - loadPeriods - Simple Period - slidingPeriod : %O", slidingPeriod);

                    this.period.Duration = slidingPeriod.Duration;
                    this.period.SelectedPeriod = slidingPeriod.Periodicity;
                    this.period.TimeUnit = this.slidingUnits.filter(u => u.Id == slidingPeriod.TimeUnit).at(0);

                    if(lastYearPeriod)
                        this.period.LastYearPeriod = lastYearPeriod;

                    if(refPeriod)
                        this.period.RefPeriod = refPeriod;

                    console.debug("MultiPeriod - loadPeriods - Simple Period - this.Periods : %O - this.period.Duration : %O ", this.Periods,  this.period.Duration);
                }
                // mode avancé
                else
                {
                    switch(parameters['Periods'][0].Periodicity){
                        case "Year":
                            this.period.SelectedYears = []

                            parameters['Periods'].forEach(element => {
                                value = parseInt(element.Start);

                                this.period.SelectedYears.push({ value: parseInt(element.Start)});
                            });  
                            
                            console.debug("MultiPeriod - loadPeriods - period.SelectedYears : %O", this.period.SelectedYears);
                            
                            this.selectedYears = this.period.SelectedYears;

                            break;
                        case "Month":
                            this.period.SelectedMonths = [];

                            parameters['Periods'].forEach(element => {
                                value = parseInt(element.Start.split('|')[1]);

                                this.period.SelectedMonths.push({ month: element.Start.split('|')[0], year: parseInt(element.Start.split('|')[1])});
                            });
                            
                            console.debug("MultiPeriod - loadPeriods - period.SelectedMonths : %O", this.period.SelectedMonths);
                            
                            this.selectedMonths = this.period.SelectedMonths;

                            break;
                        case "Week":
                            this.period.SelectedWeeks = [];

                            parameters['Periods'].forEach(element => {
                                value = parseInt(element.Start.split('|')[1]);

                                this.period.SelectedWeeks.push({week: parseInt(element.Start.split('|')[0]), year: parseInt(element.Start.split('|')[1])});
                            }); 

                            console.debug("MultiPeriod - loadPeriods - period.SelectedWeeks : %O", this.period.SelectedWeeks);

                            this.selectedWeeks = this.period.SelectedWeeks;

                            break;
                        case "Day":
                            this.period.SelectedDays = []

                            parameters['Periods'].forEach(element => {
                                console.debug("MultiPeriod - loadPeriods - Day - element.StartDate : %O", element.StartDate);

                                console.debug("MultiPeriod - loadPeriods - Day - startDate : %O - endDate : %O", moment(element.StartDate).format('YYYY-MM-DD')
                                    , moment(element.EndDate).format('YYYY-MM-DD'));

                                this.period.SelectedDays.push({ start: moment(element.StartDate).format('YYYY-MM-DD')
                                    , end: moment(element.EndDate).format('YYYY-MM-DD') });

                                // new Date(element.StartDate).toDateString()
                            });

                            console.debug("MultiPeriod - loadPeriods - period.SelectedDays : %O", this.period.SelectedDays);

                            this.selectedDays = this.period.SelectedDays;

                            break;
                        case "Hour":
                        case "Minutes30":
                        case "Minutes10":
                            this.period.SelectedHours = []

                            parameters['Periods'].forEach(element => {
                                console.debug("Goal - loadPeriods - Hour - element.StartDate : %O", element.StartDate);
                                this.period.SelectedHours.push({
                                    start: moment(element.StartDate).format('YYYY-MM-DD'),// element.StartDate.toISOString().split('T')[0],
                                    startHour: moment(element.StartDate).format('hh:mm'), //element.StartDate.toISOString().split('T')[1]?.substring(0,5),
                                    end: moment(element.EndDate).format('YYYY-MM-DD'), // element.EndDate.toISOString().split('T')[0],
                                    endHour: moment(element.EndDate).format('hh:mm') //element.EndDate.toISOString().split('T')[1]?.substring(0,5)
                                });
                            });

                            console.debug("Goal - loadPeriods - goalPeriod.SelectedHours : %O", this.period.SelectedHours);

                            this.selectedHours = this.period.SelectedHours;

                            break;
                    }

                    this.period.SelectedPeriod = parameters['Periods'][0].Periodicity;

                    this.Periods = parameters['Periods'];
                }

                this.useTrend = parameters["Periods"].some(period => 'TrendType' in period);
            }
        }

        /**
         * Met à jour la période dans la configuration
         */
        @Watch('period', {deep : true} )
        protected updatePeriods() {
            console.debug("MultiPeriod - updatePeriods - Debut - configs[Periods] : %O | selectedPeriod : %O  ", this.configs["Periods"], this.period.SelectedPeriod);
            console.debug('MultiPeriod - UpdatePeriod', this.period);
            Vue.delete(this.configs, 'Periods');
            this.Periods = [];
            if (this.period.SelectedMode === ModeEnum.Simple) {
                let sliding : Sliding;
                //let timeUnit : string = this.slidingUnits.find(x => x.Id == this.period.SelectedPeriod).Id;            

                sliding = {Mode: "Simple", Type: "Sliding", Periodicity: this.period.SelectedPeriod, Compare: false, Duration: this.period.Duration, TimeUnit: this.period.TimeUnit.Id};

                // Ajout de la péridoe de référence
                if(this.period.RefPeriod)
                    this.Periods.push(this.period.RefPeriod);

                // Ajout de la période de l'année précédente
                if(this.period.LastYearPeriod)
                    this.Periods.push(this.period.LastYearPeriod);

                this.Periods.push(sliding);
            }
            else{
                console.debug("MultiPeriod - updatePeriods - period : %O ", this.period.SelectedYears);

                switch (this.period.SelectedPeriod) {
                    case 'Year':
                        for(let y of this.period.SelectedYears){
                            let range : Range;

                            range = { Mode: "Advanced", Type: "Range", Periodicity: this.period.SelectedPeriod, Compare: false, Start: y.value.toString(), End: y.value.toString() };
                            this.Periods.push(range);
                        }

                        this.selectedYears = this.period.SelectedYears;
                        break;
                    case 'Month':
                        for(let m of this.period.SelectedMonths){
                            let range : Range;

                            range = { Mode: "Advanced", Type: "Range", Periodicity: this.period.SelectedPeriod, Compare: false, Start: m.month+"|"+m.year, End: m.month+"|"+m.year };
                            this.Periods.push(range);
                        }

                        this.selectedMonths = this.period.SelectedMonths;
                        break;
                    case 'Week':
                        for(let w of this.period.SelectedWeeks){
                            let range : Range;
                            let week = w.week < 10 ? "0" + w.week : w.week;

                            range = { Mode: "Advanced", Type: "Range", Periodicity: this.period.SelectedPeriod, Compare: false, Start: week+"|"+w.year, End: week+"|"+w.year };
                            this.Periods.push(range);
                        }

                        this.selectedWeeks = this.period.SelectedWeeks;
                        break;
                    case 'Day':
                        for(let d of this.period.SelectedDays){
                            let dateToDate : DateToDate;

                            let start : Date = moment.utc(d.start).toDate();
                            let end : Date = moment.utc(d.end).toDate();

                            dateToDate = { Mode: "Advanced", Type: "DateToDate", Periodicity: this.period.SelectedPeriod, Compare: false, StartDate: start, EndDate: end  };
                            this.Periods.push(dateToDate);
                        }

                        this.selectedDays = this.period.SelectedDays;

                        break;
                    case 'Hour':
                    case "Minutes30":
                    case "Minutes10":
                        for (let d of this.period.SelectedHours) {
                            let dateToDate: DateToDate;

                            let start: moment.Moment = moment.utc(d.start);
                            start = start.set('hour', parseInt(d.startHour.split(':')[0]));
                            start = start.set('minute', parseInt(d.startHour.split(':')[1]));
                            let end: moment.Moment = moment.utc(d.end);
                            end = end.set('hour', parseInt(d.endHour.split(':')[0]));
                            end = end.set('minute', parseInt(d.endHour.split(':')[1]));

                            dateToDate = {
                                Mode: "Advanced",
                                Type: "DateToDate",
                                Periodicity: this.period.SelectedPeriod,
                                Compare: false,
                                StartDate: start.toDate(),
                                EndDate: end.toDate()
                            };
                                
                            this.Periods.push(dateToDate);
                        }

                        this.selectedHours = this.period.SelectedHours;
                    break;
                }
            }

            Vue.set(this.configs, 'Periods', this.Periods);
            console.debug("MultiPeriod - updatePeriods - configs[Periods] - Fin", this.configs["Periods"]);
        }

        /** Permet de générer le titre pour la partie période */
        private GeneratePeriodsTitle() {
            let title = "";

            //mode simple
            if(this.period.SelectedMode === ModeEnum.Simple)
            {
                // période sélectionnée est de type Sliding
                let period = this.configs["Periods"].find(x => x.Type.includes("Sliding"));

                console.debug("MultiPeriod - GeneratePeriodsTitle - sliding period", period);

                let duration = period.Duration;
                let periodLabel = this.slidingUnits.find(x => x.Id == period.TimeUnit).Label;
                title += duration + " " + periodLabel;

                if(this.period.LastYearPeriod)
                title += this.$t('WithLastYear').toString(); // avec année précédente

                if(this.period.RefPeriod){
                    title += this.period.LastYearPeriod ? this.$t('And').toString() : this.$t('With').toString(); // et / avec
                    title += this.periodTypes.find(x => x.Id == period.Periodicity).Label.toLowerCase() + " ";

                    let refPeriod : Range = (this.period.RefPeriod as Range);

                    console.debug("MultiPeriod - GeneratePeriodsTitle - refPeriod", refPeriod);

                    switch(refPeriod.Periodicity){
                        case "Year":
                        title += refPeriod.Start;
                            break;
                        case "Month":
                            let monthDate = refPeriod.Start.split('|')[1] + "-" + refPeriod.Start.split('|')[0]+"-01";
                            title += moment(monthDate).format("MMMM YYYY");
                            break;
                        case "Week":
                        title += refPeriod.Start.replace('|',' ');
                            break;
                    }
                    title += " en référence"; // en référence
                }
            }
            // mode avancé
            else
            {
                //Type de période
                let periodLabel = this.periodTypes.find(x => x.Id == this.configs["Periods"].at(0).Periodicity).Label;

                if(this.configs["Periods"].length > 1 && !periodLabel.endsWith('s'))
                    periodLabel += "s";

                title += periodLabel + " ";

                for (let i = 0; i < this.configs["Periods"].length; i++) {
                    
                    switch(this.configs["Periods"].at(i).Periodicity){
                        case "Year":
                            title += this.configs["Periods"].at(i).Start;
                            break;
                        case "Month":
                        title += this.configs["Periods"].at(i).Start.replace('|', '/');
                            break;
                        case "Week":
                        title += this.configs["Periods"].at(i).Start.replace('|', '-');
                            break;
                        case "Day":
                        title += "(" + moment(this.configs["Periods"].at(i).StartDate).format('DD/MM/YYYY') + " - " + moment(this.configs["Periods"].at(i).EndDate).format('DD/MM/YYYY') + ")";
                            break;
                    }
                    title += " - ";
                }

                title = title.slice(0, -2);
            }

            return title;
        }

        /** Permet de vérifier si les périodes renseignés sont invalides */
        public CheckPeriodsInvalidity() : boolean {
            console.debug("MultiPeriod - CheckPeriodsInvalidity - Periods : %O - this.selectedMode : %O", this.configs["Periods"], this.period.SelectedMode);

            // selon type de période => pas même nom/type de paramètres
            let sameDate : boolean = false;

            // Période de référence est présente dans la période année précédente
            let isPeriodExist : boolean = false;
            
            // Indication si la durée est vide
            let isDurationEmpty : boolean = false;

            /** Message concernant la période de référence */
            let periodMessage : string = "";

            /** Reset de la validation de la période de référence */
            this.refValid = null;

            /** Reset de la validation pour la valeur de durée en mode simple */
            this.durationValid = null;

            /** Reset de la validation pour la valeur des périodes en mode avancé */
            this.periodValid = null;

            console.debug("MultiPeriod - CheckPeriodsInvalidity - SelectedMode : ", ModeEnum[this.period.SelectedMode]);

            // Vérification Valeur existante pour la durée en mode simple
            if(this.period.SelectedMode === ModeEnum.Simple && (!this.period.Duration || this.period.Duration.toString().trim().length < 1)){    
                isDurationEmpty = true;
            }

            // Vérification date identiques quand plusieurs périodes sélectionnées
            if (this.configs && 'Periods' in this.configs && this.configs["Periods"].length >= 1) {
                console.debug("MultiPeriod - CheckPeriodsInvalidity - configs[Periods] : %O | Type : %O | SelectedMode : %O", this.configs["Periods"], this.configs["Periods"].at(0).Type, this.period.SelectedMode);

                // Traitement pour le mode avancé 
                if(this.period.SelectedMode === ModeEnum.Advanced)
                {
                    // Récupération du type de période (pouvant etre format DCx.xxx.yyyy.Range apres retour serveur)
                    let periodTypes = this.configs["Periods"][0].Type.split('.');
                    let pType = periodTypes[periodTypes.length - 1];

                    // Vérification période renseignées identiques
                    switch (pType) {
                        case "Range":
                            // Permet de calculer le nombre d'élement ayant la même date de début (ca Start = End)
                            let filteredRange =  this.configs["Periods"].reduce((x,y) => {
                                x[y.Start] = (x[y.Start] || 0 ) + 1;
                                return x;
                            }, {});

                            // Filtre si au moins 2 périodes idnetiques sont renseignées
                            sameDate = this.configs["Periods"].filter(x => filteredRange[x.Start] > 1).length > 1;
                            
                            console.debug("MultiPeriod - CheckPeriodsInvalidity - Range | sameDate", sameDate);
                            break;
                        case "DateToDate":
                            // Vérification dates de départ sup. à celle de fin
                            sameDate = this.configs["Periods"].some(x => moment(x.StartDate).isAfter(x.EndDate));
                            
                            console.debug("MultiPeriod - CheckPeriodsInvalidity - DateToDate | sameDate - Start", sameDate);

                            // Si il n'y a pas de périodes ayant Start > End, 
                            if(!sameDate){
                                this.configs["Periods"].forEach((el) => {
                                let start = el.StartDate;
                                let end = el.EndDate;
                                
                                // périodes filtrées ( sans element courant )
                                let filteredPeriods = this.configs["Periods"].filter(x => x.StartDate != start && x.EndDate != end);
                                
                                // vérification intervalle de dates 
                                filteredPeriods.forEach(el => {
                                    let startBetween = moment(start).isBetween(el.StartDate, el.EndDate, undefined, "[]");
                                    let endBetween = moment(end).isBetween(el.StartDate, el.EndDate, undefined, "[]");

                                    sameDate ||= startBetween || endBetween;
                                });
                            });
                            }
                            
                            console.debug("MultiPeriod - CheckPeriodsInvalidity - DateToDate | sameDate", sameDate);
                        break;
                        default:
                            break;
                    }
                }
                // Traitement pour le mode simple
                else{
                    // Verification de la durée saisie si elle est plus grande que la durée de lensemble des données
                    let selectedDuration : number = this.configs["Periods"][0].Duration;
                    let diffYear : number = (this.today.getFullYear() - this.startYear) + 1;
                    let result : number = 0;    

                    switch (this.configs["Periods"][0].TimeUnit) {
                        case "Year":
                            result = selectedDuration - diffYear;
                            break;
                        case "Month":
                            result = (selectedDuration / 12) - diffYear;
                        break;
                        case "Week":
                            result = (selectedDuration / 52) - diffYear;
                        break;
                        case "Day":
                            result = (selectedDuration / 365) - diffYear;
                        break;    
                    }

                    this.isDurationGreater = result > 0;

                    // Vérification si période de référence est renseignée
                    if(this.period.RefPeriod){

                        // Vérification si période principale est valide
                        let sliding : Sliding = this.configs["Periods"].find(x => String(x.Type).includes("Sliding")); 
                        //Période de référence (on prend que Start car End est identique)
                        let periodRef = (this.period.RefPeriod as Range).Start;
                        let end = moment().format("YYYY-MM-DD");
                        let start : string;
                        let momentRef: string;                 

                        console.debug("MultiPeriod - CheckPeriodsInvalidity - periodRef - Start : %O | period - SelectedPeriod : %O", periodRef, this.period.SelectedPeriod);

                        // transformation selon type de période 
                        switch(this.period.TimeUnit.Id){
                            case "Year":
                                start = moment().subtract((sliding.Duration - 1), 'years').format("YYYY-MM-DD");
                                momentRef = this.getMomentOfRefPeriod(this.period.RefPeriod as Range)
                                console.debug("MultiPeriod - CheckPeriodsInvalidity - Year - PeriodRef | SlidingPeriod - start : %s - momentRef : %s - end : %s", start, momentRef, end);
                            break;  
                            case 'Month':
                                start = moment().subtract((sliding.Duration - 1), 'months').format("YYYY-MM-DD");
                                // -1 car les mois vont de 0 à 11
                                momentRef = this.getMomentOfRefPeriod(this.period.RefPeriod as Range)
                                console.debug("MultiPeriod - CheckPeriodsInvalidity - Month - PeriodRef | SlidingPeriod - start : %s - momentRef : %s - end : %s", start, momentRef, end);
                            break;
                            case 'Week':
                                start = moment().subtract((sliding.Duration - 1), 'weeks').format("YYYY-MM-DD");
                                momentRef = this.getMomentOfRefPeriod(this.period.RefPeriod as Range)
                                console.debug("MultiPeriod - CheckPeriodsInvalidity - Week - PeriodRef | SlidingPeriod - start : %s - momentRef : %s - end : %s", start, momentRef, end);
                            break;
                            case 'Day':
                                start = moment().subtract((sliding.Duration - 1), 'days').format("YYYY-MM-DD");
                                momentRef = this.getMomentOfRefPeriod(this.period.RefPeriod as Range)
                                console.debug("MultiPeriod - CheckPeriodsInvalidity - Day - PeriodRef | SlidingPeriod - start : %s - momentRef : %s - end : %s", start, momentRef, end);
                            break;                                                 
                        }
                    
                        console.debug("MultiPeriod - CheckPeriodsInvalidity - PeriodRef | SlidingPeriod - isBetween", moment(momentRef).isBetween(end, start, undefined, "[]"));

                        // 3eme arg => correspond à la graduation de comparaison (defaut en milliseconds)
                        // 4eme arg => correspond inclusion/exclusion dates bornes --> ( : exclusion | [ : inclusion (cf. doc momentjs)
                        isPeriodExist = moment(momentRef).isBetween(start, end, undefined, "[]");

                        // Message période référence existe dans la période renseignée
                        if(isPeriodExist)
                            periodMessage = this.$t('Errors.ReferenceInPeriod').toString()// "La période de référence ne peut pas être présente dans la période renseignée";

                        // Vérification si année précédente est sélectionnée ET que la période de référence n'existe pas dans la période renseignée
                        if(this.period.LastYearPeriod && !isPeriodExist){
                            //Annee prec.
                            let lastYearPeriod : SlidingTrend = this.configs["Periods"].find(x => String(x.Type).includes("SlidingTrend"));

                            console.debug("MultiPeriod - CheckPeriodsInvalidity - lastYearPeriod : %O | PeriodRef : %O", lastYearPeriod, periodRef);

                            end = moment().year(Number(lastYearPeriod.Year)).format('YYYY-MM-DD');

                            switch(this.period.SelectedPeriod){
                                case 'Day':
                                    start = moment().subtract((sliding.Duration - 1), 'days').format("YYYY-MM-DD");
                                    momentRef = this.getMomentOfRefPeriod(this.period.RefPeriod as Range)
                                    console.debug("MultiPeriod - CheckPeriodsInvalidity - Day - PeriodRef | SlidingPeriod - start : %s - momentRef : %s - end : %s", start, momentRef, end);
                                break;  
                                case 'Week':
                                    start = moment().year(Number(lastYearPeriod.Year)).subtract((lastYearPeriod.Duration - 1), 'weeks').format("YYYY-MM-DD");
                                    momentRef = moment().weeks(Number(periodRef.split('|')[0])).year(Number(periodRef.split('|')[1])).format("YYYY-MM-DD");
                                    console.debug("MultiPeriod - CheckPeriodsInvalidity - Week - PeriodRef | SlidingPeriod - start : %s - momentRef : %s - end : %s", start, momentRef, end);
                                break;
                                case 'Month':
                                    start = moment().year(Number(lastYearPeriod.Year)).subtract((sliding.Duration - 1), 'months').format("YYYY-MM-DD");
                                    // -1 car les mois vont de 0 à 11
                                    momentRef = moment().month(Number(periodRef.split('|')[0]) - 1).year(Number(periodRef.split('|')[1])).format("YYYY-MM-DD");
                                    console.debug("MultiPeriod - CheckPeriodsInvalidity - Month - PeriodRef | SlidingPeriod - start : %s - momentRef : %s - end : %s", start, momentRef, end);
                                    break;
                                case "Year":
                                    start = moment().year(Number(lastYearPeriod.Year)).subtract((sliding.Duration - 1), 'years').format("YYYY-MM-DD");
                                    momentRef = moment().year(Number(periodRef)).format("YYYY-MM-DD");
                                    console.debug("MultiPeriod - CheckPeriodsInvalidity - Year - PeriodRef | SlidingPeriod - start : %s - momentRef : %s - end : %s", start, momentRef, end);
                                    break;
                            }

                            // 3eme arg => correspond à la graduation de comparaison (defaut en milliseconds)
                            // 4eme arg => correspond inclusion/exclusion dates bornes --> ( : exclusion | [ : inclusion (cf. doc momentjs)
                            isPeriodExist = moment(momentRef).isBetween(start, end, undefined, "[]");

                            // Message période référence existe dans la période année précédente
                            if(isPeriodExist)
                                periodMessage = this.$t('Errors.ReferenceInLastYear').toString() // "La période de référence ne peut pas être présente dans la période de l'année précédente";
                        }
                    }
                }    
            }

            if(this.isDurationGreater){
                this.durationValid = false;
                this.errorList.push({ Message: this.$t('Errors.DurationTooLarge').toString() }) // "La durée de la période glissante sélectionnée est plus grande que l'ensemble des données disponibles"
            }

            if(sameDate){
                this.periodValid = false;
                this.errorList.push({Message: this.$t('Errors.DuplicatePeriods').toString()}) //"Veuillez renseigner des périodes qui ne sont pas identiques"
            }

            if(isPeriodExist){
                this.refValid = false;
                this.errorList.push({Message: periodMessage})
            }

            console.debug("MultiPeriod - CheckPeriodsInvalidity - isPeriodExist : %O - this.errorList : %O", isPeriodExist, this.errorList);

            if(isDurationEmpty){
                this.durationValid = false;
                this.errorList.push({Message: this.$t('Errors.EmptyDuration').toString()})//"Veuillez renseigner une valeur pour la durée de la période"
            }

            return sameDate || isPeriodExist || isDurationEmpty;
        }

        // Création de l'objet moment sur la période de référence
        private getMomentOfRefPeriod(periodRef: Range): string {
            switch(periodRef.Periodicity){
                case "Year":
                    return moment().year(Number(periodRef.Start)).endOf('year').format("YYYY-MM-DD");
                case "Month":
                    return moment().year(Number(periodRef.Start.split('|')[1])).month(Number(periodRef.Start.split('|')[0]) - 1).endOf('month').format("YYYY-MM-DD");
                case "Week":
                    return moment().year(Number(periodRef.Start.split('|')[1])).weeks(Number(periodRef.Start.split('|')[0])).format("YYYY-MM-DD");
                case "Day":
                    return moment(this.refDay).format("YYYY-MM-DD");
            }
        }

        /** Permet de créer la liste des périodes avancées par défaut */
        private initdefaultAdvancedPeriods(){
                // ajout des années par défaut
                this.selectedYears = [];
                
                for (let index = 0; index < this.advancedPeriodsDefaultLength; index++) {
                    console.debug("MultiPeriod - initdefaultAdvancedPeriods - availableYears", this.availableYears[index]);
                    if(this.availableYears[index])
                        this.selectedYears.push({ value : this.availableYears[index].Id});
                }

                this.period.SelectedYears = this.selectedYears;

                console.debug("MultiPeriod - initdefaultAdvancedPeriods - selectedYears", this.selectedYears);

                //ajout des mois par défaut
                this.selectedMonths = [];

                for (let index = 0; index < this.advancedPeriodsDefaultLength; index++) {
                    if(this.availableYears[index])
                        this.selectedMonths.push({month: this.availableMonths[0].Id, year: this.availableYears[index].Id});
                }

                this.period.SelectedMonths = this.selectedMonths;

                console.debug("MultiPeriod - initdefaultAdvancedPeriods - selectedMonths", this.selectedMonths);

                //ajout des semaines par defaut
                this.selectedWeeks = [];

                for (let index = 0; index < this.advancedPeriodsDefaultLength; index++) {
                    if(this.availableYears[index])
                        this.selectedWeeks.push({ week: 1, year: this.availableYears[index].Id });
                }

                this.period.SelectedWeeks = this.selectedWeeks;

                console.debug("MultiPeriod - initdefaultAdvancedPeriods - selectedWeeks", this.selectedWeeks);

                this.period.SelectedDays = this.selectedDays.slice(0, this.advancedPeriodsDefaultLength);
        }

        /** 
         * Pointeur vers fonction pour désactiver watchers
         * @see https://vuejs.org/guide/essentials/watchers#stopping-a-watcher
         **/
         private unwatchGetPerimeterItems = undefined;

        /** Initialisation du composant après sélection d'une fonction Analysis */
        @Watch('configs.Feature', { immediate: true })
        private init(newVal, oldVal){
            if ("Feature" in this.configs && this.configs["Feature"] && newVal != oldVal) {
                /** Initialisation de la machine d'état de périodes */
                this.initStateMachine(newVal, oldVal).then(() => {
                    console.debug("MultiPeriod - init - initStateMachine - configs[Period]", this.configs['Periods'])

                    /** Permet d'initialiser les différentes périodes par défaut à partir la date la plus ancienne coté serveur : la StartDate */
                    this.unwatchGetPerimeterItems = this.$store.watch((state, getters) => getters['GetPerimeterItems']('StartDate'), date => {
                        console.debug("MultiPeriod - created - date : %O, date.length > 0 && this.startYear == 0 : %O", date, date.length > 0 && this.startYear == 0);

                        // Récupération des informations par défaut à l'ouverture du composant
                        if(date.length > 0){
                            this.startYear = Number(date.at(0).split("-")[0]);
                            this.availableYears = this.getYears(this.startYear);
                            this.initdefaultAdvancedPeriods();

                            // Chargement des périodes de la configuration (éxécuter à l'interieur du watch pour éviter ecrasement des périodes enregistrées) 
                            this.filteredPeriods = this.periodTypes.filter(s => this.availablePeriods.includes(s.Id));
                            this.loadPeriods(this.configs);
                        }
                    }, { immediate: true });                   
                });
            }
        }

        // created() {
        //     console.debug("MultiPeriod - created - configs : %O - configs['Periods'] : %O", this.configs, this.configs['Periods']);
        // }

        beforeDestroy() {
            // Libère les watchers
            if (this.unwatchGetPerimeterItems)
                this.unwatchGetPerimeterItems();
        }
    }

    /** Classe représentant les multipériodes */
    export class Period{
        SelectedMode: ModeEnum;
        SelectedPeriod : string;
        Duration: number;
        LastYearPeriod : BasePeriod;
        RefPeriod: BasePeriod;
        SelectedYears: { value: number}[];
        SelectedMonths: { month: string, year: number}[];
        SelectedWeeks: { week: number, year: number }[];
        SelectedDays: { start: string, end: string }[];
        SelectedHours : { start: string, startHour: string, end: string, endHour: string }[];
        TimeUnit: { Id: string, Label: string };
    }

</script>

<style scoped>
</style>